import React, { useEffect, useMemo, useState } from 'react'
import {
  AppDialog,
  Button,
  EmptyHubState,
  MiniLoader,
  Select,
  StatusPill,
  TextInput,
  type SelectOption,
} from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import { formatMoney } from '../../../utils/formatMoney'
import {
  listEduDues,
  recordEduFeePayment,
  type EduDueData,
} from '../../../services/eduFeeService'
import {
  isDueOverdue,
  sortDuesOverdueFirst,
  createFeePaymentRequestId,
  validatePaymentInput,
  getTodayDateIso,
} from '../../Education/feeHelpers'

export interface ClassFeesPanelProps {
  workspaceId: string
  classId?: string
  currency?: string | null
  onPaymentRecorded?: (receiptNo: string) => void
}

export const ClassFeesPanel: React.FC<ClassFeesPanelProps> = ({
  workspaceId,
  classId,
  currency = 'USD',
  onPaymentRecorded,
}) => {
  const { t } = useNls()

  const [dues, setDues] = useState<EduDueData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [successReceipt, setSuccessReceipt] = useState<string | null>(null)

  // Payment dialog state
  const [payingDue, setPayingDue] = useState<EduDueData | null>(null)
  const [method, setMethod] = useState<'CASH' | 'UPI' | 'BANK'>('CASH')
  const [reference, setReference] = useState<string>('')
  const [amountInput, setAmountInput] = useState<string>('')
  const [paidOn, setPaidOn] = useState<string>(getTodayDateIso())
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const loadDues = async () => {
    if (!workspaceId) return
    setLoading(true)
    setError(null)
    try {
      const res = await listEduDues({
        workspaceId,
        nodeId: classId || undefined,
      })
      const openDues = (res.dues || []).filter(
        (d) => d.status !== 'PAID' && d.status !== 'WAIVED' && (d.balanceMinor ?? 0) > 0,
      )
      setDues(sortDuesOverdueFirst(openDues))
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to load fee dues')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadDues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, classId])

  const openMarkPaidDialog = (due: EduDueData) => {
    setPayingDue(due)
    setMethod('CASH')
    setReference('')
    const defaultAmount = ((due.balanceMinor ?? 0) / 100).toFixed(2)
    setAmountInput(defaultAmount)
    setPaidOn(getTodayDateIso())
    setValidationError(null)
  }

  const closeDialog = () => {
    setPayingDue(null)
    setValidationError(null)
  }

  const methodOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: 'CASH', label: t('NLSEducationFeesPaymentMethodCash') },
      { value: 'UPI', label: t('NLSEducationFeesPaymentMethodUpi') },
      { value: 'BANK', label: t('NLSEducationFeesPaymentMethodBank') },
    ],
    [t],
  )

  const handleConfirmPayment = () => {
    if (!payingDue || !workspaceId) return

    const parsedAmount = parseFloat(amountInput)
    const validation = validatePaymentInput({
      method,
      reference,
      amountReceived: isNaN(parsedAmount) ? 0 : parsedAmount,
      balanceMinor: payingDue.balanceMinor ?? 0,
      paidOn,
      currency: currency || null,
    })

    if (!validation.valid && validation.errorKey) {
      setValidationError(t(validation.errorKey))
      return
    }

    setSubmitting(true)
    setValidationError(null)
    void (async () => {
      try {
        const amountMinor = Math.round(parsedAmount * 100)
        const requestId = createFeePaymentRequestId()
        const res = await recordEduFeePayment({
          workspaceId,
          learnerId: payingDue.learnerId,
          allocations: [{ dueId: payingDue.id, amountMinor }],
          method,
          reference: reference.trim() || undefined,
          paidOn,
          requestId,
        })

        setSuccessReceipt(res.receiptNo)
        onPaymentRecorded?.(res.receiptNo)
        closeDialog()
        await loadDues()
      } catch (err: unknown) {
        setValidationError((err as Error)?.message || 'Failed to record fee payment')
      } finally {
        setSubmitting(false)
      }
    })()
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
        <MiniLoader />
      </div>
    )
  }

  return (
    <div className="classFeesPanel" style={{ padding: '12px 16px' }} data-testid="class-fees-panel">
      {successReceipt && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: '12px',
            backgroundColor: 'var(--surface-card)',
            borderRadius: '8px',
            color: 'var(--success)',
            fontSize: '13px',
          }}
          data-testid="fee-receipt-notice"
        >
          {t('NLSEduMobileFeesReceiptSent', { receiptNo: successReceipt })}
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '12px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {dues.length === 0 ? (
        <EmptyHubState
          title={t('NLSEduMobileFeesEmptyTitle')}
          description={t('NLSEduMobileFeesEmptyBody')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="fees-dues-list">
          {dues.map((due) => {
            const overdue = isDueOverdue(due)
            return (
              <div
                key={due.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: overdue
                    ? '1px solid var(--danger)'
                    : '1px solid var(--border-subtle)',
                  backgroundColor: 'var(--surface-card)',
                }}
                data-testid={`fee-due-row-${due.id}`}
                data-overdue={overdue ? 'true' : 'false'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                      {due.learnerName || due.learnerId}
                    </div>
                    {(due.label ?? due.title) && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {due.label ?? due.title}
                      </div>
                    )}
                  </div>
                  <StatusPill
                    tone={overdue ? 'danger' : 'warning'}
                  >
                    {overdue ? t('NLSEduMobileFeesOverdueBadge') : due.status}
                  </StatusPill>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>
                    {currency ? formatMoney(due.balanceMinor ?? 0, currency) : `${due.balanceMinor ?? 0}`}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openMarkPaidDialog(due)}
                    data-testid={`mark-paid-btn-${due.id}`}
                  >
                    {t('NLSEduMobileFeesMarkPaid')}
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Mark Paid Dialog */}
      {payingDue && (
        <AppDialog
          open={Boolean(payingDue)}
          onDismiss={closeDialog}
          title={t('NLSEducationFeesRecordPaymentTitle')}
          actions={[
            {
              label: t('NLSEduMobileFeesMarkPaid'),
              onClick: handleConfirmPayment,
              variant: 'primary',
              disabled: submitting,
              testId: 'confirm-payment-btn',
            },
          ]}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '280px' }}>
            {validationError && (
              <div style={{ color: 'var(--danger)', fontSize: '12px' }}>
                {validationError}
              </div>
            )}

            <div>
              <label style={{ fontSize: '12px', fontWeight: 500 }}>
                {t('NLSEducationFeesPaymentMethodLabel')}
              </label>
              <Select<string>
                value={method}
                onChange={(val) => setMethod(String(val) as 'CASH' | 'UPI' | 'BANK')}
                options={methodOptions}
                ariaLabel={t('NLSEducationFeesPaymentMethodLabel')}
              />
            </div>

            {(method === 'UPI' || method === 'BANK') && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>
                  {t('NLSEducationFeesPaymentReferenceLabel')} *
                </label>
                <TextInput
                  value={reference}
                  onChange={(val) => setReference(val)}
                  placeholder={t('NLSEducationFeesPaymentReferencePlaceholder')}
                  aria-label={t('NLSEducationFeesPaymentReferenceLabel')}
                />
              </div>
            )}

            <div>
              <label style={{ fontSize: '12px', fontWeight: 500 }}>
                {t('NLSEducationFeesPaymentAmountReceivedLabel')}
              </label>
              <TextInput
                value={amountInput}
                onChange={(val) => setAmountInput(val)}
                type="number"
                aria-label={t('NLSEducationFeesPaymentAmountReceivedLabel')}
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 500 }}>
                {t('NLSEducationFeesPaymentPaidOnLabel')}
              </label>
              <TextInput
                value={paidOn}
                onChange={(val) => setPaidOn(val)}
                type="date"
                aria-label={t('NLSEducationFeesPaymentPaidOnLabel')}
              />
            </div>
          </div>
        </AppDialog>
      )}
    </div>
  )
}
